import * as borogove from "borogove";
import Picker from "@emoji-mart/react";
import encodeQR from "qr";
import { useState } from "react";
import {
	Button,
	ChatContainer,
	MessageList,
} from "@chatscope/chat-ui-kit-react";
import {
	Menu,
	MenuItem,
	ListSubheader,
	Popover,
	CircularProgress,
} from "@mui/material";
import { useOutletContext } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCircleXmark,
	faCheck,
	faFaceSmile,
	faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { EditableProfile } from "./EditableProfile";
import { confirmDialog } from "./ConfirmDialog";

const timeInFuture = (seconds: number) => {
	return new Date(new Date().getTime() + seconds * 1000).toLocaleString([], {
		timeStyle: "short",
	});
};

export const Account = () => {
	const { active, client, status, setStatus, logout } = useOutletContext<{
		active: boolean;
		client: borogove.Client;
		status: string;
		setStatus: (status: string) => void;
		logout: (completely: boolean) => void;
	}>();
	const selfChat = client.getChat(client.accountId());
	const self = selfChat?.getParticipantDetails(client.accountId());

	const confirmThenLogout = () => {
		confirmDialog({
			message:
				"Do you want to also delete all chats and messages from this device?",
			accept: () => logout(true),
			reject: () => logout(false),
		});
	};

	const [statusPopover, setStatusPopover] = useState<HTMLElement | null>(null);
	const [statusExpireMenu, setStatusExpireMenu] = useState<HTMLElement | null>(
		null,
	);
	const [statusEmoji, setStatusEmoji] = useState(selfChat?.status?.emoji ?? "");
	const [statusText, setStatusText] = useState(selfChat?.status?.text ?? "");

	return (
		<ChatContainer className={active ? "active" : undefined}>
			<MessageList className="account">
				<MessageList.Content>
					<h1>Account Details</h1>
					<code>{client.accountId()}</code>

					<AvatarWithPlaceholder
						displayName={client.displayName()}
						placeholderUri={self?.placeholderUri}
						photoUri={self?.photoUri}
					/>

					<div className="status">
						<Button
							className="emoji"
							onClick={(e) => setStatusPopover(e.currentTarget)}
						>
							{statusEmoji || <FontAwesomeIcon icon={faFaceSmile} />}
						</Button>
						<input
							type="text"
							value={statusText}
							onChange={(e) => setStatusText(e.target.value)}
							placeholder="Set a custom status"
						/>
						{(statusText || statusEmoji) && (
							<Button
								icon={
									<FontAwesomeIcon
										icon={faCircleXmark}
										onClick={() => {
											setStatusEmoji("");
											setStatusText("");
											client.setStatus(new borogove.Status("", ""));
										}}
									/>
								}
							/>
						)}
						<Button
							icon={<FontAwesomeIcon icon={faCheck} />}
							onClick={(e) => {
								setStatusExpireMenu(e.currentTarget);
							}}
						/>
						<Popover
							open={!!statusPopover}
							anchorEl={statusPopover}
							onClose={() => setStatusPopover(null)}
						>
							<Picker
								data={async () => ({ ...(await import("@emoji-mart/data")) })}
								categories={[
									"frequent",
									"activity",
									"places",
									"objects",
									"people",
									"nature",
									"foods",
									"symbols",
									"flags",
								]}
								maxFrequentRows={1}
								onEmojiSelect={(emoji: { native: string }) => {
									setStatusEmoji(emoji.native);
									setStatusPopover(null);
								}}
							/>
						</Popover>
						<Menu
							open={!!statusExpireMenu}
							anchorEl={statusExpireMenu}
							onClose={() => setStatusExpireMenu(null)}
						>
							<ListSubheader>Set status for</ListSubheader>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										86400,
									);
									setStatusExpireMenu(null);
								}}
							>
								24 hours (tomorrow at {timeInFuture(86400)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										14400,
									);
									setStatusExpireMenu(null);
								}}
							>
								4 hours ({timeInFuture(14400)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										3600,
									);
									setStatusExpireMenu(null);
								}}
							>
								1 hours ({timeInFuture(3600)})
							</MenuItem>
							<MenuItem
								onClick={() => {
									client.setStatus(
										new borogove.Status(statusEmoji, statusText),
										1800,
									);
									setStatusExpireMenu(null);
								}}
							>
								30 minutes ({timeInFuture(1800)})
							</MenuItem>
						</Menu>
					</div>

					{self && (
						<AwaitOnce
							key={client.accountId()}
							resolve={() => self.profile(client)}
							fallback={<CircularProgress />}
						>
							{(profile) => (
								<EditableProfile
									initial={profile}
									saveProfile={(profile, pub) =>
										client.setProfile(profile, pub)
									}
								/>
							)}
						</AwaitOnce>
					)}

					<Button
						icon={<FontAwesomeIcon icon={faRightFromBracket} />}
						onClick={confirmThenLogout}
					>
						Remove this account
					</Button>

					{status === "auth" && (
						<form
							onSubmit={(e) => {
								e.preventDefault();
								client.usePassword(e.currentTarget.password.value);
								setStatus("connecting");
							}}
						>
							Please enter your password again:
							<input type="password" name="password" />
							<button>Login</button>
						</form>
					)}

					<div
						className="qrcode"
						dangerouslySetInnerHTML={{
							__html: encodeQR(
								"xmpp:" + encodeURIComponent(client.accountId()),
								"svg",
								{ ecc: "high" },
							),
						}}
					/>
				</MessageList.Content>
			</MessageList>
		</ChatContainer>
	);
};
